import { useUnsavedPromptInModal } from '@common/UnsavedPromptInModal.hooks';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { Button, ComponentType, LoadingButton, ModalBodyExtras, useForm, useModal } from '@gcl/react-core';
import * as React from 'react';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { useCreateMyGems, useUpdateMyGems } from './myGems.queries';
import { ICreateMyGemsRequest, IMyGems, IUpdateMyGemsRequest } from './myGems.models';
import { MyGemsEditForm } from './MyGemsEditForm';
import { EMyGemsFormKeys } from './myGems.constants';

export interface IUseMyGemsEditModalProps {
  myGems?: IMyGems;
  parent?: IMyGems;
}

export interface IRenderMyGemsEditModalProps {}

export const useMyGemsEditModal = ({ myGems, parent }: IUseMyGemsEditModalProps) => {
  const { openPortal, closePortal: closeMyGemsEditPortal, renderModal } = useModal();
  const closePortal = React.useRef(closeMyGemsEditPortal);

  const queryClient = useQueryClient();

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const isEdit = React.useMemo(() => {
    return myGems && isPositiveNumber(myGems.id);
  }, [JSON.stringify(myGems)]);

  const renderMyGemsEditModal = ({}: IRenderMyGemsEditModalProps) => {
    const {
      mutate: mutateCreateMyGems,
      isLoading: isLoadingCreateMyGems,
      isError: isErrorCreateMyGems,
      isSuccess: isSuccessCreateMyGems,
      reset: resetCreateMyGems,
      error: errorCreateMyGems,
    } = useCreateMyGems();

    const {
      mutate: mutateUpdateMyGems,
      isLoading: isLoadingUpdateMyGems,
      isError: isErrorUpdateMyGems,
      isSuccess: isSuccessUpdateMyGems,
      reset: resetUpdateMyGems,
      error: errorUpdateMyGems,
    } = useUpdateMyGems();

    React.useEffect(() => {
      if (!isSuccessCreateMyGems) return;
      closePortal.current();
      resetCreateMyGems();
    }, [isSuccessCreateMyGems]);

    const { isFormDirty, setIsFormDirty, confirmUnsavedPrompt } = useUnsavedPromptInModal();

    closePortal.current = () => {
      useFormMethods.reset({});
      closeMyGemsEditPortal();
    };

    const handleSubmit = (data: any) => {
      const createRequest: ICreateMyGemsRequest = {
        ...data,
        parentId: parent?.id,
        [EMyGemsFormKeys.SEQUENCE]: Number(data[EMyGemsFormKeys.SEQUENCE]) ?? 0,
      };

      if (!isEdit) {
        mutateCreateMyGems(createRequest);
        return;
      }

      if (!myGems) return;

      const updateRequest: IUpdateMyGemsRequest = { ...createRequest, id: myGems?.id, rowVersion: myGems?.rowVersion };
      mutateUpdateMyGems(updateRequest);
    };
    return renderModal({
      onHide: () => {
        useFormMethods.reset({});
      },
      large: true,
      backdrop: isFormDirty ? 'static' : undefined,
      title: `${isEdit ? 'Edit' : 'Add'} My Gems`,
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <>
            <ApiSuccessToast
              successes={[
                {
                  isSuccess: isSuccessCreateMyGems,
                  message: 'Create My GEMS success.',
                  reset: resetCreateMyGems,
                },
                {
                  isSuccess: isSuccessUpdateMyGems,
                  message: 'Update My GEMS success.',
                  reset: resetUpdateMyGems,
                },
              ]}
              callbackAfterToasted={() => {
                queryClient.invalidateQueries([FETCH_QUERIES_NAME.MY_GEMS]);
                closePortal.current();
              }}
            />
            <ApiErrorDisplay
              setFormError={useFormMethods.setError}
              errors={[
                { error: errorCreateMyGems, genericError: 'Create My GEMS failed', reset: resetCreateMyGems },
                { error: errorUpdateMyGems, genericError: 'Update My GEMS failed', reset: resetUpdateMyGems },
              ]}
            />
            <MyGemsEditForm setIsFormDirty={setIsFormDirty} useFormMethods={useFormMethods} myGems={myGems} />
          </>
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        const onClickCancel = () => {
          if (confirmUnsavedPrompt() && onHide) {
            onHide();
          }
        };
        return (
          <>
            <LoadingButton
              type={ComponentType.Primary}
              onClick={() => {
                useFormMethods.handleSubmit(handleSubmit)();
              }}
              isLoading={isEdit ? isLoadingUpdateMyGems : isLoadingCreateMyGems}
              isError={isEdit ? isErrorUpdateMyGems : isErrorCreateMyGems}
              isSuccess={isEdit ? isSuccessUpdateMyGems : isSuccessCreateMyGems}
              timeToResetSuccessState={3000}
              timeToResetErrorState={5000}
              prefixIconName="FolderCheck"
            >
              Save
            </LoadingButton>
            <Button
              prefixIconName="ChevronLeft"
              type={ComponentType.Secondary}
              onClick={onClickCancel}
              style={{ margin: '10px' }}
            >
              Cancel
            </Button>
          </>
        );
      },
    });
  };
  return {
    openPortal,
    closePortal: closePortal.current,
    renderModal: renderMyGemsEditModal,
  };
};
